import { useContext, useState } from "react";
import { RawInput } from "./RawInput";
import { FormContext } from "./Form";
import { InferType, ObjectSchema } from "yup";
import { FormState } from "./Form.types";
import { isRequired } from "./utils";

type InputProps = {
    name: string;
    label: string;
    number?: boolean;
    readonly?: boolean;
    parent?: string;
    index?: number;
    placeholder?: string;
    disabled?: boolean;
    mask?: string;
    maskChar?: string | null;
    tooltip?: string;
};

export function Input<T extends ObjectSchema<any>>({
    number = false,
    readonly = false,
    parent = "",
    ...props
}: InputProps) {
    const [touched, setTouched] = useState(false);
    const name = parent ? `${parent}.${props.name}` : props.name;
    const form = useContext(FormContext) as FormState<T> | undefined;

    if (!form) return null;

    const error = form.errors?.[name] || "";
    const showError =
        touched && error && !error.toLowerCase().includes("required");

    return (
        <RawInput
            {...props}
            name={name}
            readOnly={readonly}
            mask={props.mask}
            maskChar={props.maskChar}
            placeholder={props.placeholder}
            required={isRequired(form.schema, name)}
            onChange={(e) => {
                let value: string | number = e.target.value;
                if (number) {
                    const stringValue = e.target.value;
                    if (
                        stringValue === "" ||
                        stringValue === "-" ||
                        stringValue.endsWith(".")
                    ) {
                        value = stringValue;
                    } else {
                        const numValue = parseFloat(stringValue);
                        value = isNaN(numValue) ? stringValue : numValue;
                    }
                }

                setTouched(true);
                form.updateValue(name as InferType<T>, value);
            }}
            disabled={props.disabled}
            onBlur={() => setTouched(true)}
            value={form.values[name] || ""}
            errorMessage={showError ? form.errors?.[name] : undefined}
            showErrorRing={Boolean(touched && error)}
            tooltip={props.tooltip}
        />
    );
}
